import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Box, Paper, Tab, Tabs } from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useDispatch, useStore } from "react-redux";
import { theme } from "../../Theme";
import PerformanceIntelligence from "./PerformanceIntelligence";
import ScenarioSimulator from "./ScenarioSimulator";
import ScenarioComparison from "./ScenarioComparison";
import PlannerCalendar from "./PlannerCalendar";
import OptimizerResults from "./OptimizerResults";
import DetailsDrawer from "./components/DetailsDrawer";
import BrandSummaryStrip from "./components/BrandSummaryStrip";
import { resetSimulatorInputs } from "../../Redux/promoSimulatorSlice/promoSimulatorSlice";

const TABS = [
  {
    label: "Performance Intelligence",
    icon: <InsightsOutlinedIcon fontSize="small" />,
  },
  { label: "Simulator", icon: <ScienceOutlinedIcon fontSize="small" /> },
  // { label: "Comparison", icon: <CompareArrowsOutlinedIcon fontSize="small" /> }, 
  { label: "Planner", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { label: "Optimizer", icon: <AutoFixHighIcon fontSize="small" /> },
];

export default function PromoSimulator() {
  const [tab, setTab] = useState(0);
  const [simulatorMounted, setSimulatorMounted] = useState(false);
  const [plannerMounted, setPlannerMounted] = useState(false);
  const [optimizerMounted, setOptimizerMounted] = useState(false);
  const dispatch = useDispatch();
  const store = useStore();
  const didResetSimulatorOnFirstOpenRef = useRef(false);

  useEffect(() => {
    if (tab !== 1) return;
    setSimulatorMounted(true);
    if (!didResetSimulatorOnFirstOpenRef.current) {
      const batchQueue = store.getState().promoSimulator?.batchSimulationQueue;
      const hasBatchQueue = Array.isArray(batchQueue) && batchQueue.length > 0;
      if (!hasBatchQueue) {
        dispatch(resetSimulatorInputs());
      }
      didResetSimulatorOnFirstOpenRef.current = true;
    }
  }, [tab]);

  useLayoutEffect(() => {
    if (tab !== 1) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [tab]);

  useEffect(() => {
    if (tab !== 2) return;
    setPlannerMounted(true);
  }, [tab]);

  useEffect(() => {
    if (tab !== 3) return;
    setOptimizerMounted(true);
  }, [tab]);

  return (
    <Box>
      {/* Tab navigation */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: "1px solid #F2F4F7",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13,
              minHeight: 48,
              gap: 0.5,
            },
            "& .Mui-selected": {
              fontWeight: 700,
              color: `${theme.palette.primary.main} !important`,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          {TABS.map(({ label, icon }) => (
            <Tab key={label} label={label} icon={icon} iconPosition="start" />
          ))}
        </Tabs>
      </Paper>

      {/* Screen content */}
      <Box>
        {tab === 0 && <PerformanceIntelligence onSwitchTab={setTab} />}
        {simulatorMounted && (
          <Box
            sx={{ display: tab === 1 ? "block" : "none" }}
            aria-hidden={tab !== 1}
          >
            <BrandSummaryStrip />
            <ScenarioSimulator />
            <Box mt={4}>
              <ScenarioComparison />
            </Box>
          </Box>
        )}
        {plannerMounted && (
          <Box
            sx={{ display: tab === 2 ? "block" : "none" }}
            aria-hidden={tab !== 2}
          >
            <PlannerCalendar />
          </Box>
        )}
        {optimizerMounted && (
          <Box
            sx={{ display: tab === 3 ? "block" : "none" }}
            aria-hidden={tab !== 3}
          >
            <OptimizerResults />
          </Box>
        )}
      </Box>

      {/* Global details drawer — accessible from any tab */}
      <DetailsDrawer />
    </Box>
  );
}
